import { ref } from 'vue';
import { defineStore } from 'pinia';
import {
  TaskStatus,
  type ReviewProjectAnalysisInDb,
  type ReviewProjectAnalysisStatistics,
  type ReviewProjectFileInDb,
  type ReviewProjectFileStatistics,
  type ReviewProjectQuestionStatistics,
  type ReviewProjectReportInDb,
  type ReviewProjectReportTaskGroupSummary,
  type ReviewProjectSeenStatistics,
  type ReviewProjectTagStatistics,
} from '@/api';
import {
  ReviewProjectService,
  type ReviewProjectTaskGroupDetail,
  type ReviewProjectTaskGroupSummary,
} from '@/services/reviewProjectService';
import type { EvaluationColumnFilter } from '@/composables/usePlaybookRunCells';
import type { GaiaSaveStateValue } from '@/components/design/composite/GaiaSaveState.vue';
import { logger } from '@/utils/logger';

/**
 * Shared state of the review project workspace panes (data room, preview,
 * analysis, chat). Any pane may read or write. The single currency for
 * cross-pane file references is the vault file revision uid: it is what the
 * PDF preview renders, what playbook runs are pinned to, and what citations
 * (page + highlighted text) are relative to.
 *
 * The data room and analysis selections are independent of each other and
 * never drive the preview. Selection exists to drag multiple files onto an
 * analysis and, later, to tell the chat pane what to talk about; the preview
 * is only ever pointed somewhere explicitly (showPreview / showCitation).
 *
 * Pane components unmount whenever their surface goes away — the data room
 * collapsing or switching view, the report or the chat being closed — so all
 * UI state that should survive that lives here rather than in the panes.
 */
export interface DataRoomFilesTableState {
  search: string;
  page: number;
  pageSize: number;
  activeFilters: Record<string, unknown>;
}

function createFilesTableState(): DataRoomFilesTableState {
  return { search: '', page: 1, pageSize: 10, activeFilters: {} };
}

export interface AnalysisDataTableState {
  page: number;
  pageSize: number;
  /** Evaluation column filters; an empty array on a dimension means "no filter". */
  columnFilter: EvaluationColumnFilter;
}

export interface QuestionsTableState {
  search: string;
  answeredFilter: 'all' | 'open' | 'answered';
  page: number;
  pageSize: number;
}

function createQuestionsTableState(): QuestionsTableState {
  return { search: '', answeredFilter: 'all', page: 1, pageSize: 10 };
}

/** What the preview pane shows: a revision, optionally opened at a citation. */
export interface PreviewTarget {
  revisionUid: string;
  page: number | null;
  highlights: string[];
}

/**
 * What the notes pane edits. The project uid is captured at open time so a
 * flush of a dirty draft can still persist after the detail view unmounted.
 */
export interface NotesTarget {
  projectUid: string;
  fileUid: string;
  fileName: string;
}

/**
 * Aliased rather than restated: `GaiaSaveState` is what renders these, so the
 * set of states a store may report is the set that strip knows how to draw.
 */
export type NotesSaveState = GaiaSaveStateValue;

export const useReviewProjectStore = defineStore('reviewProject', () => {
  // State
  // Selection in the data room (Files table + Raw Data vault share it).
  const dataRoomSelectedRevisionUids = ref<string[]>([]);
  // Bumped when something outside the files table adds data-room files (e.g. a chat
  // composer upload); the table watches it and refetches — the list endpoint syncs
  // the data room first, so the new file is in the response.
  const dataRoomFilesRefreshTick = ref(0);
  // Cumulative revision uid → review-project file uid map, filled as files-table
  // pages load; lets auto-tag translate a revision-uid selection into file uids.
  const dataRoomFileUidByRevisionUid = ref<Record<string, string>>({});
  // Same pattern for display names: lets the chat show which files a message
  // referenced without a lookup round-trip (the chat only holds revision uids).
  const dataRoomFileNameByRevisionUid = ref<Record<string, string>>({});
  const previewTarget = ref<PreviewTarget | null>(null);
  const dataRoomTab = ref<string>('files');
  const dataRoomDirectoryUid = ref<string | null>(null);
  const dataRoomFilesTableState = ref<Record<'usable' | 'unusable', DataRoomFilesTableState>>({
    usable: createFilesTableState(),
    unusable: createFilesTableState(),
  });
  // null = not fetched yet; the analysis pane loads once per project, not per remount.
  const analyses = ref<ReviewProjectAnalysisInDb[] | null>(null);
  const analysisTabUid = ref<string | null>(null);
  // Analyses whose tab currently shows the settings view instead of the data view.
  const analysisSettingsUids = ref<string[]>([]);
  const analysisDataTableState = ref<Record<string, AnalysisDataTableState>>({});
  // Checked run uids per analysis uid; independent of the data room selection.
  const analysisSelectedRunUids = ref<Record<string, string[]>>({});
  // Tasks pane: null = not fetched yet; the pane loads the group list once per project.
  const taskGroups = ref<ReviewProjectTaskGroupSummary[] | null>(null);
  // null = show the group list; set = drill-down into that group's tasks.
  const selectedTaskGroupUid = ref<string | null>(null);
  // Cache of fetched group detail (with tasks) so re-entering a group needn't refetch.
  const taskGroupDetails = ref<Record<string, ReviewProjectTaskGroupDetail>>({});
  // Statistics tab: null = not fetched yet; each section caches independently.
  const fileStatistics = ref<ReviewProjectFileStatistics | null>(null);
  const seenStatistics = ref<ReviewProjectSeenStatistics | null>(null);
  const tagStatistics = ref<ReviewProjectTagStatistics | null>(null);
  const analysisStatistics = ref<ReviewProjectAnalysisStatistics | null>(null);
  // Shared by the Q&A tab's progress strip and the Statistics tab; Q&A mutations
  // set it back to null so both refetch.
  const questionStatistics = ref<ReviewProjectQuestionStatistics | null>(null);
  // Q&A tab search/filter/pagination — survives the pane remount on tab switch.
  const questionsTableState = ref<QuestionsTableState>(createQuestionsTableState());
  // null = not fetched yet; while null, report_updated websocket events are ignored
  // (the report UI's first fetch loads fresh anyway).
  const report = ref<ReviewProjectReportInDb | null>(null);
  // Report pane draft/save machinery: same shape (and reasons) as the notes below.
  const reportDraft = ref('');
  const reportSaveState = ref<NotesSaveState>('saved');
  // Soft lock: true while a report-generation task group is (believed) running.
  // Advisory only — the backend accepts saves during a run (last-write-wins);
  // the lock just keeps this client from editing under the agent.
  const reportGenerationLocked = ref(false);
  // Failure notice for the report pane banner; the user dismisses it.
  const reportGenerationError = ref<string | null>(null);

  const REPORT_DEBOUNCE_MS = 5000;
  let reportTimer: ReturnType<typeof setTimeout> | null = null;
  // Last value known persisted; draft ≠ this = dirty.
  let reportPersistedValue = '';
  // Serializes saves AND websocket refetch GETs: a GET enqueued behind an
  // in-flight PUT reads-after-write, so a stale response can never revert a
  // newer save (which the editor would then dutifully re-persist).
  let reportChain: Promise<void> = Promise.resolve();
  // Captured at init so a flush can outlive the pane/view; chained callbacks
  // re-check it and bail after a project switch.
  let reportProjectUid: string | null = null;
  // First fetch in flight — the pane remounts on layout changes and must not
  // start a second one.
  let reportInitStarted = false;

  const TASK_GROUP_POLL_MS = 5000;
  // After an interrupt confirm the lock is optimistic: how long to wait for the
  // run's group to show up before concluding the run never started (resume
  // failed, tool declined, concurrent-run 400).
  const REPORT_LOCK_GRACE_MS = 30_000;
  let taskGroupPollTimer: ReturnType<typeof setInterval> | null = null;
  // Serializes list fetches so an older response can never overwrite a newer
  // one; capped at one in flight plus one queued (poll ticks beyond that add
  // no information).
  let taskGroupChain: Promise<void> = Promise.resolve();
  let taskGroupSyncQueue = 0;
  // Chained callbacks re-check this and bail after a project switch.
  let taskGroupProjectUid: string | null = null;
  // Uid of the running report group once observed; null during the optimistic
  // grace phase after a confirm.
  let watchedReportGroupUid: string | null = null;
  // Epoch-ms deadline of the optimistic phase; null once a run is observed.
  let reportLockGraceDeadline: number | null = null;
  // Report-group uids that existed at confirm time: a uid outside this set is
  // the confirmed run even if it only ever shows up already terminal (a run
  // can finish between two polls).
  let knownReportGroupUids: Set<string> | null = null;

  function clearReportTimer(): void {
    if (reportTimer !== null) {
      clearTimeout(reportTimer);
      reportTimer = null;
    }
  }

  function setReportDraft(value: string): void {
    reportDraft.value = value;
    if (reportProjectUid === null) return;
    clearReportTimer();
    if (value === reportPersistedValue) {
      reportSaveState.value = 'saved';
      return;
    }
    reportSaveState.value = 'pending';
    reportTimer = setTimeout(flushReport, REPORT_DEBOUNCE_MS);
  }

  /**
   * Persist the draft now if dirty. Snapshots uid and draft synchronously and
   * advances the persisted value optimistically, so a second flush of the same
   * value is a no-op (mirrors flushNotes).
   */
  function flushReport(): void {
    clearReportTimer();
    const uid = reportProjectUid;
    if (uid === null || reportDraft.value === reportPersistedValue) return;
    const value = reportDraft.value;
    const previous = reportPersistedValue;
    reportPersistedValue = value;
    reportSaveState.value = 'saving';
    reportChain = reportChain.then(async () => {
      try {
        const updated = await ReviewProjectService.setReport(uid, value);
        if (reportProjectUid !== uid) return;
        // Fresh updated_at without relying on the websocket echo.
        report.value = updated;
        if (reportDraft.value === value) reportSaveState.value = 'saved';
      } catch (error) {
        logger.error('Failed to save report:', error);
        if (reportProjectUid === uid) {
          // The draft counts as dirty again, so the next edit or flush retries.
          reportPersistedValue = previous;
          reportSaveState.value = 'error';
        }
      }
    });
  }

  /** First load for the report pane; the backend auto-creates an empty report. */
  async function initReport(projectUid: string): Promise<void> {
    if (report.value !== null || reportInitStarted) return;
    reportInitStarted = true;
    reportProjectUid = projectUid;
    try {
      const fetched = await ReviewProjectService.getReport(projectUid);
      if (reportProjectUid !== projectUid) return;
      report.value = fetched;
      reportDraft.value = fetched.content;
      reportPersistedValue = fetched.content;
      reportSaveState.value = 'saved';
      // Opening the project mid-run must lock the editor right away.
      syncReportGeneration(projectUid);
    } catch (error) {
      // Cleared so a pane remount (or the retry button) fetches again.
      if (reportProjectUid === projectUid) reportInitStarted = false;
      throw error;
    }
  }

  /**
   * Websocket "report changed": refetch, serialized on the save chain (see
   * reportChain). The event carries no content and is also echoed for this
   * client's own saves, so:
   * - fetched content === persisted → own echo / no change: refresh metadata
   *   only, never touch the draft (no editor reset, no cursor jump, no re-save);
   * - dirty or mid-save → drop the remote content; the local draft wins and the
   *   next flush overwrites it (full-content last-write-wins);
   * - otherwise apply. CKEditor may normalize the applied HTML and save it back
   *   once; that echo then matches the persisted value and stops the cycle.
   */
  function refetchReport(): void {
    const uid = reportProjectUid;
    if (uid === null || report.value === null) return;
    // Re-checked after each await: the project may be switched or the report
    // cleared mid-flight (TS narrowing goes stale across awaits, so this lives
    // in a helper the compiler can't narrow away).
    const stale = (): boolean => reportProjectUid !== uid || report.value === null;
    reportChain = reportChain.then(async () => {
      if (stale()) return;
      try {
        const fetched = await ReviewProjectService.getReport(uid);
        if (stale()) return;
        if (fetched.content === reportPersistedValue) {
          report.value = fetched;
          return;
        }
        if (reportSaveState.value !== 'saved' || reportDraft.value !== reportPersistedValue) {
          return;
        }
        report.value = fetched;
        reportDraft.value = fetched.content;
        reportPersistedValue = fetched.content;
      } catch (error) {
        logger.error('Failed to refresh report:', error);
      }
    });
  }

  function startTaskGroupPolling(projectUid: string): void {
    if (taskGroupPollTimer !== null) return;
    taskGroupPollTimer = setInterval(() => {
      syncReportGeneration(projectUid);
    }, TASK_GROUP_POLL_MS);
  }

  function stopTaskGroupPolling(): void {
    if (taskGroupPollTimer !== null) {
      clearInterval(taskGroupPollTimer);
      taskGroupPollTimer = null;
    }
  }

  /** Release the soft lock; null message = success or a run that never started. */
  function finishReportGenerationRun(errorMessage: string | null): void {
    reportGenerationLocked.value = false;
    reportGenerationError.value = errorMessage;
    watchedReportGroupUid = null;
    reportLockGraceDeadline = null;
    knownReportGroupUids = null;
    // On success this pulls the agent's write even if the websocket echo was
    // missed; on failure it is a harmless no-change fetch.
    refetchReport();
  }

  function reportRunFailureMessage(status: TaskStatus | undefined): string | null {
    if (status === TaskStatus.FAILED) {
      return 'Report generation failed. The report was left unchanged.';
    }
    if (status === TaskStatus.PARTIAL) {
      return 'Report generation finished with errors.';
    }
    return null;
  }

  /** Reconcile the soft lock against the freshly fetched task-group list. */
  function reconcileReportGeneration(
    projectUid: string,
    groups: ReviewProjectTaskGroupSummary[]
  ): void {
    // Publishing the list here also keeps the Tasks pane live while a run is polled.
    taskGroups.value = groups;
    const reportGroups = groups.filter(
      (group): group is ReviewProjectReportTaskGroupSummary => group.task_type === 'report'
    );
    const running = reportGroups.find(group => group.status === TaskStatus.RUNNING);

    if (watchedReportGroupUid !== null) {
      const watched = reportGroups.find(group => group.uid === watchedReportGroupUid);
      if (watched === undefined || watched.status !== TaskStatus.RUNNING) {
        finishReportGenerationRun(reportRunFailureMessage(watched?.status));
      }
    } else if (running !== undefined) {
      // Covers opening the project mid-run and the optimistic phase resolving.
      watchedReportGroupUid = running.uid;
      reportLockGraceDeadline = null;
      knownReportGroupUids = null;
      reportGenerationLocked.value = true;
    } else if (reportGenerationLocked.value) {
      // Optimistic phase: the confirmed run hasn't been observed running yet.
      const seenUids = knownReportGroupUids;
      const fresh =
        seenUids === null ? undefined : reportGroups.find(group => !seenUids.has(group.uid));
      if (fresh !== undefined) {
        // The run started and finished between two polls.
        finishReportGenerationRun(reportRunFailureMessage(fresh.status));
      } else if (reportLockGraceDeadline !== null && Date.now() > reportLockGraceDeadline) {
        // The run never materialized; unlock silently.
        finishReportGenerationRun(null);
      }
    }

    if (reportGenerationLocked.value) startTaskGroupPolling(projectUid);
    else stopTaskGroupPolling();
  }

  /**
   * Fetch the task-group list and reconcile the report soft lock against the
   * observed statuses — the single source of truth for the lock (the websocket
   * only announces successful writes, never starts or failures). Triggered by
   * the report pane's first load, an interrupt confirm, report_updated events
   * while locked, and each poll tick.
   */
  function syncReportGeneration(projectUid: string): void {
    taskGroupProjectUid = projectUid;
    if (taskGroupSyncQueue >= 2) return;
    taskGroupSyncQueue += 1;
    taskGroupChain = taskGroupChain.then(async () => {
      try {
        if (taskGroupProjectUid !== projectUid) return;
        let groups: ReviewProjectTaskGroupSummary[];
        try {
          groups = await ReviewProjectService.listTaskGroups(projectUid);
        } catch (error) {
          // A transient fetch failure must not release the lock; the next
          // trigger or poll tick retries.
          logger.error('Failed to refresh task groups:', error);
          return;
        }
        if (taskGroupProjectUid !== projectUid) return;
        reconcileReportGeneration(projectUid, groups);
      } finally {
        taskGroupSyncQueue -= 1;
      }
    });
  }

  /**
   * The user confirmed a report_generation_confirm chat interrupt: the run is
   * about to be dispatched server-side. Lock optimistically; the poll adopts
   * the run once it appears (or the grace period releases the lock if the
   * dispatch never happened).
   */
  function notifyReportGenerationStarted(projectUid: string): void {
    if (reportGenerationLocked.value) return;
    // A pending debounced save persists first: the PUT is serialized on
    // reportChain ahead of any refetch, and the editor is read-only from here.
    flushReport();
    reportGenerationError.value = null;
    reportGenerationLocked.value = true;
    watchedReportGroupUid = null;
    knownReportGroupUids =
      taskGroups.value === null
        ? null
        : new Set(
            taskGroups.value.filter(group => group.task_type === 'report').map(group => group.uid)
          );
    reportLockGraceDeadline = Date.now() + REPORT_LOCK_GRACE_MS;
    syncReportGeneration(projectUid);
  }

  /**
   * Websocket "report changed": refetch the content; while locked also re-check
   * the run — the event alone is ambiguous (another user's manual save fires it
   * too), so only an observed terminal group status may unlock.
   */
  function handleReportStale(projectUid: string): void {
    refetchReport();
    if (reportGenerationLocked.value) syncReportGeneration(projectUid);
  }

  // Notes pane. Draft, save state AND the debounce timer live here because the
  // pane component unmounts whenever its drawer closes — a component-local
  // timer would die with it and the pending save would be lost.
  const notesTarget = ref<NotesTarget | null>(null);
  const notesDraft = ref('');
  const notesSaveState = ref<NotesSaveState>('saved');
  // Every successful notes save publishes the PUT response here; the files table
  // watches it and patches its row (a websocket echo back to the saving client
  // is not guaranteed).
  const savedFileUpdate = ref<ReviewProjectFileInDb | null>(null);

  const NOTES_DEBOUNCE_MS = 5000;
  let notesTimer: ReturnType<typeof setTimeout> | null = null;
  // Last value known persisted for the current target; draft ≠ this = dirty.
  let notesPersistedValue = '';
  // Serializes PUTs so a flush for the previous file can't interleave with one
  // for the next.
  let notesChain: Promise<void> = Promise.resolve();

  function clearNotesTimer(): void {
    if (notesTimer !== null) {
      clearTimeout(notesTimer);
      notesTimer = null;
    }
  }

  function setNotesDraft(value: string): void {
    notesDraft.value = value;
    if (!notesTarget.value) return;
    clearNotesTimer();
    if (value === notesPersistedValue) {
      notesSaveState.value = 'saved';
      return;
    }
    notesSaveState.value = 'pending';
    notesTimer = setTimeout(flushNotes, NOTES_DEBOUNCE_MS);
  }

  /**
   * Persist the draft now if dirty. Snapshots target and draft synchronously,
   * so callers may immediately swap or clear the target. The persisted value
   * advances optimistically at enqueue time, making a second flush of the same
   * value a no-op (close right after the timer fired saves exactly once).
   */
  function flushNotes(): void {
    clearNotesTimer();
    const target = notesTarget.value;
    if (!target || notesDraft.value === notesPersistedValue) return;
    const value = notesDraft.value;
    const previous = notesPersistedValue;
    notesPersistedValue = value;
    notesSaveState.value = 'saving';
    notesChain = notesChain.then(async () => {
      try {
        const updated = await ReviewProjectService.updateFile(target.projectUid, target.fileUid, {
          // The backend clears notes on explicit null; absent leaves them untouched.
          notes: value === '' ? null : value,
        });
        savedFileUpdate.value = updated;
        if (notesTarget.value?.fileUid === target.fileUid && notesDraft.value === value) {
          notesSaveState.value = 'saved';
        }
      } catch (error) {
        logger.error('Failed to save file notes:', error);
        if (notesTarget.value?.fileUid === target.fileUid) {
          // The draft counts as dirty again, so the next edit or flush retries.
          notesPersistedValue = previous;
          notesSaveState.value = 'error';
        }
      }
    });
  }

  /**
   * Open the notes pane for a file, seeding the draft from row data (no fetch).
   * A dirty draft for a previously open file flushes first.
   */
  function showNotes(target: NotesTarget, initialNotes: string): void {
    // Re-opening the same file keeps unsaved edits.
    if (notesTarget.value?.fileUid === target.fileUid) return;
    flushNotes();
    notesTarget.value = target;
    notesDraft.value = initialNotes;
    notesPersistedValue = initialNotes;
    notesSaveState.value = 'saved';
  }

  function closeNotes(): void {
    flushNotes();
    notesTarget.value = null;
    notesDraft.value = '';
    notesPersistedValue = '';
    notesSaveState.value = 'saved';
  }

  // Actions
  /** Point the preview at a revision's first page. */
  function showPreview(revisionUid: string): void {
    previewTarget.value = { revisionUid, page: null, highlights: [] };
  }

  /** Point the preview at a citation. */
  function showCitation(target: PreviewTarget): void {
    previewTarget.value = target;
  }

  function closePreview(): void {
    previewTarget.value = null;
  }

  /**
   * Replace the analysis list with a fresh fetch (websocket "list changed" events)
   * and drop per-analysis UI state of analyses that no longer exist. Run selections
   * survive so a burst of updates doesn't clear what the user checked.
   */
  function setAnalyses(next: ReviewProjectAnalysisInDb[]): void {
    analyses.value = next;
    const uids = new Set(next.map(analysis => analysis.uid));
    analysisSettingsUids.value = analysisSettingsUids.value.filter(uid => uids.has(uid));
    for (const uid of Object.keys(analysisDataTableState.value)) {
      if (!uids.has(uid)) delete analysisDataTableState.value[uid];
    }
    for (const uid of Object.keys(analysisSelectedRunUids.value)) {
      if (!uids.has(uid)) delete analysisSelectedRunUids.value[uid];
    }
    if (analysisTabUid.value === null || !uids.has(analysisTabUid.value)) {
      analysisTabUid.value = next[0]?.uid ?? null;
    }
  }

  function reset(): void {
    // A dirty notes draft of the previous project must persist, not vanish.
    closeNotes();
    savedFileUpdate.value = null;
    dataRoomSelectedRevisionUids.value = [];
    dataRoomFileUidByRevisionUid.value = {};
    dataRoomFileNameByRevisionUid.value = {};
    previewTarget.value = null;
    dataRoomTab.value = 'files';
    dataRoomDirectoryUid.value = null;
    dataRoomFilesTableState.value = {
      usable: createFilesTableState(),
      unusable: createFilesTableState(),
    };
    analyses.value = null;
    analysisTabUid.value = null;
    analysisSettingsUids.value = [];
    analysisDataTableState.value = {};
    analysisSelectedRunUids.value = {};
    taskGroups.value = null;
    selectedTaskGroupUid.value = null;
    taskGroupDetails.value = {};
    fileStatistics.value = null;
    seenStatistics.value = null;
    tagStatistics.value = null;
    analysisStatistics.value = null;
    questionStatistics.value = null;
    questionsTableState.value = createQuestionsTableState();
    // A dirty report draft of the previous project flushes to its captured uid.
    flushReport();
    clearReportTimer();
    report.value = null;
    reportDraft.value = '';
    reportPersistedValue = '';
    reportSaveState.value = 'saved';
    // In-flight chained callbacks for the old project bail on the uid mismatch.
    reportProjectUid = null;
    reportInitStarted = false;
    stopTaskGroupPolling();
    reportGenerationLocked.value = false;
    reportGenerationError.value = null;
    watchedReportGroupUid = null;
    reportLockGraceDeadline = null;
    knownReportGroupUids = null;
    // In-flight task-group syncs for the old project bail on the uid mismatch.
    taskGroupProjectUid = null;
  }

  return {
    dataRoomSelectedRevisionUids,
    dataRoomFilesRefreshTick,
    dataRoomFileUidByRevisionUid,
    dataRoomFileNameByRevisionUid,
    previewTarget,
    dataRoomTab,
    dataRoomDirectoryUid,
    dataRoomFilesTableState,
    analyses,
    analysisTabUid,
    analysisSettingsUids,
    analysisDataTableState,
    analysisSelectedRunUids,
    taskGroups,
    selectedTaskGroupUid,
    taskGroupDetails,
    fileStatistics,
    seenStatistics,
    tagStatistics,
    analysisStatistics,
    questionStatistics,
    questionsTableState,
    report,
    reportDraft,
    reportSaveState,
    reportGenerationLocked,
    reportGenerationError,
    setReportDraft,
    flushReport,
    initReport,
    refetchReport,
    notifyReportGenerationStarted,
    handleReportStale,
    stopTaskGroupPolling,
    notesTarget,
    notesDraft,
    notesSaveState,
    savedFileUpdate,
    setNotesDraft,
    flushNotes,
    showNotes,
    closeNotes,
    showPreview,
    showCitation,
    closePreview,
    setAnalyses,
    reset,
  };
});
